import type { CSSProperties } from 'react';
import { designImage, qrImage } from './marketingRules';
import type { MarketingDesign, Palette } from '../../core/types/marketing';
import type { Product } from '../../core/types/catalog';

/**
 * Bouwstenen voor de ontwerpen. Alles in ontwerp-pixels en inline stijl,
 * zodat de download exact gelijk is aan wat Sarah ziet.
 */

export type DesignProps = {
  d: MarketingDesign;
  p: Palette;
  w: number;
  h: number;
  city: string;
  /** Adres en telefoon uit de instellingen, voor print. */
  contact: string[];
  product?: Product;
  routine?: {
    name: string;
    items: Product[];
    price: string;
  };
  price?: string;
};
export const serif = "'Fraunces Variable', 'Fraunces', Georgia, serif";
export const sans = "'Inter', system-ui, sans-serif";

/** Het vlak van elk ontwerp: vaste maat, eigen achtergrond, inhoud in een kolom. */
export const frame = (w: number, h: number, bg: string): CSSProperties => ({
  width: w,
  height: h,
  background: bg,
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column'
});
export function Img({
  src,
  style,
  fit = 'cover',
  width = 1400
}: {
  src: string;
  style?: CSSProperties;
  fit?: 'cover' | 'contain';
  width?: number;
}) {
  if (!src) return <div style={{
    background: 'rgba(0,0,0,0.06)',
    ...style
  }} data-id="element-2849" data-mp="2of2ztijez7lx" />;
  return <img src={designImage(src, width, fit === 'cover' ? 'jpg' : 'png')} alt="" crossOrigin="anonymous" draggable={false} style={{
    display: 'block',
    width: '100%',
    height: '100%',
    objectFit: fit,
    ...style
  }} data-id="element-2850" data-mp="2t7cpcxgma5no" />;
}

/** Het woordmerk in letters: scherp op elke achtergrond, ook donker. */
export function Wordmark({
  size,
  color,
  sub,
  city
}: {
  size: number;
  color: string;
  sub?: string;
  city: string;
}) {
  return <div style={{
    color,
    lineHeight: 1
  }} data-id="element-2851" data-mp="3c4xbvxvwss54">
      <div style={{
      fontFamily: serif,
      fontSize: size,
      fontWeight: 400,
      letterSpacing: '-0.01em'
    }} data-id="element-2852" data-mp="1ct4dpxxdxgqt">Beauty Bewust</div>
      <div style={{
      fontFamily: sans,
      fontSize: size * 0.42,
      marginTop: size * 0.28
    }} data-id="element-2853" data-mp="1hsqq655e2syk">{sub ?? `huidspecialist · ${city}`}</div>
    </div>;
}

/** Kop met cursief accent op een eigen regel. */
export function Headline({
  d,
  p,
  size,
  accentSize,
  maxWidth
}: {
  d: MarketingDesign;
  p: Palette;
  size: number;
  accentSize?: number;
  maxWidth?: number | string;
}) {
  return <h2 style={{
    fontFamily: serif,
    fontWeight: 300,
    fontSize: size,
    lineHeight: 1.04,
    letterSpacing: '-0.015em',
    color: p.fg,
    margin: 0,
    maxWidth
  }} data-id="element-2854" data-mp="1epjghqeqkocs">
      {d.headline}
      {d.accent && <span style={{
      display: 'block',
      fontStyle: 'italic',
      color: p.accent,
      fontSize: accentSize ?? size
    }} data-id="element-2855" data-mp="1omfsw77r9umm">{d.accent}</span>}
    </h2>;
}
export function Eyebrow({
  text,
  p,
  size
}: {
  text: string;
  p: Palette;
  size: number;
}) {
  if (!text) return null;
  return <p style={{
    fontFamily: sans,
    fontSize: size,
    fontWeight: 500,
    color: p.accent,
    margin: 0,
    lineHeight: 1.3
  }} data-id="element-2856" data-mp="2cemmwzq0dpiv">{text}</p>;
}
export function Body({
  text,
  p,
  size,
  maxWidth
}: {
  text: string;
  p: Palette;
  size: number;
  maxWidth?: number | string;
}) {
  if (!text) return null;
  return <p style={{
    fontFamily: sans,
    fontSize: size,
    lineHeight: 1.45,
    fontWeight: 300,
    color: p.fg,
    margin: 0,
    maxWidth,
    whiteSpace: 'pre-line'
  }} data-id="element-2857" data-mp="2v13wjipaygwq">
      {text}
    </p>;
}
export function Qr({
  url,
  p,
  size,
  onSoft = false
}: {
  url: string;
  p: Palette;
  size: number;
  onSoft?: boolean;
}) {
  if (!url) return null;
  const bg = onSoft ? p.soft : p.bg;
  return <img src={qrImage(url, p.fg, bg)} alt="" crossOrigin="anonymous" draggable={false} style={{
    width: size,
    height: size,
    display: 'block'
  }} data-id="element-2858" data-mp="2c5dqnke910dk" />;
}

/** Een boogvorm — terug te vinden in de website (de 'arch'). */
export const arch = (w: number): CSSProperties => ({
  borderTopLeftRadius: w / 2,
  borderTopRightRadius: w / 2,
  overflow: 'hidden'
});